import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePrinter } from '../../contexts/PrinterContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSave } = FiIcons;

const PrinterForm = ({ printer, onClose }) => {
  const { addPrinter, updatePrinter } = usePrinter();
  const [formData, setFormData] = useState({
    name: printer?.name || '',
    type: printer?.type || 'thermal',
    ipAddress: printer?.ipAddress || '',
    port: printer?.port || 9100,
    description: printer?.description || ''
  });
  const [error, setError] = useState('');

  const printerTypes = [
    { value: 'thermal', label: 'Thermal Receipt Printer' },
    { value: 'inkjet', label: 'Inkjet Printer' },
    { value: 'laser', label: 'Laser Printer' },
    { value: 'usb', label: 'USB Printer' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateIP = (ip) => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Printer name is required');
      return;
    }

    if (formData.type !== 'usb' && !validateIP(formData.ipAddress)) {
      setError('Please enter a valid IP address');
      return;
    }

    if (formData.port < 1 || formData.port > 65535) {
      setError('Port must be between 1 and 65535');
      return;
    }

    const printerData = {
      ...formData,
      name: formData.name.trim(),
      port: parseInt(formData.port)
    };

    const result = printer 
      ? updatePrinter(printer.id, printerData)
      : addPrinter(printerData);

    if (result.success) {
      onClose();
    } else {
      setError(result.error);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-warm-brown/10 p-6 flex items-center justify-between">
            <h2 className="text-2xl font-playfair text-warm-brown">
              {printer ? 'Edit Printer' : 'Add New Printer'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-cream rounded-full transition-colors"
            >
              <SafeIcon icon={FiX} className="text-warm-brown text-xl" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
              >
                {error}
              </motion.div>
            )}

            {/* Basic Information */}
            <div className="space-y-4">
              <div>
                <label className="block text-warm-brown font-medium mb-2">Printer Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Kitchen Printer, Counter Printer"
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                />
              </div>

              <div>
                <label className="block text-warm-brown font-medium mb-2">Printer Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                >
                  {printerTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-warm-brown font-medium mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={2}
                  placeholder="Optional description or notes about this printer"
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                />
              </div>
            </div>

            {/* Network Settings */}
            {formData.type !== 'usb' && (
              <div className="space-y-4">
                <h3 className="text-lg font-playfair text-warm-brown">Network Settings</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-warm-brown font-medium mb-2">IP Address *</label>
                    <input
                      type="text"
                      required
                      value={formData.ipAddress}
                      onChange={(e) => handleInputChange('ipAddress', e.target.value)}
                      placeholder="192.168.1.100"
                      className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                    />
                  </div>
                  <div>
                    <label className="block text-warm-brown font-medium mb-2">Port</label>
                    <input
                      type="number"
                      min="1"
                      max="65535"
                      value={formData.port}
                      onChange={(e) => handleInputChange('port', e.target.value)}
                      className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                    />
                    <p className="text-xs text-warm-brown/60 mt-1">
                      Common ports: 9100 (RAW), 515 (LPR), 631 (IPP)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* USB Settings Info */}
            {formData.type === 'usb' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">USB Printer Setup</h4>
                <p className="text-sm text-blue-700">
                  USB printers require additional setup and may need specific drivers. 
                  Ensure the printer is connected and recognized by your system before testing.
                </p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="flex-1 bg-warm-brown text-cream py-3 rounded-lg font-medium hover:bg-warm-brown/90 transition-colors flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiSave} className="text-sm" />
                {printer ? 'Update Printer' : 'Add Printer'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 border border-warm-brown/20 text-warm-brown py-3 rounded-lg font-medium hover:bg-warm-brown/5 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PrinterForm;